import React, { useEffect, useState } from "react";
import { Tooltip } from "antd";
import dayjs from "dayjs";
import { FiFileText } from "react-icons/fi";
import { formatCEP, formatCNPJ, formatValueBrl } from "../../../../../../../function/utilis/function";
import styles from "./GroupEmpresa.module.css";

const OPCOES_ENQUADRAMENTO = [
  { value: "MEI", label: "MEI - MicroEmpreendedor Individual" },
  { value: "ME", label: "ME - MicroEmpresa" },
  { value: "EPP", label: "EPP - Empresa de Pequeno Porte" },
  { value: "NORMAL", label: "Normal" },
  { value: "PA", label: "PA - Profissional Autônomo" },
  { value: "PL", label: "PL - Profissional Liberal" },
];

const OPCOES_REGIME_TRIBUTARIO = [
  { value: "SIMEI", label: "SIMEI - Sistema de Recolhimento MEI" },
  { value: "SN", label: "SN - Simples Nacional" },
  { value: "LP", label: "LP - Lucro Presumido" },
  { value: "LR", label: "LR - Lucro Real" },
  { value: "RA", label: "RA - Regime de Autônomo" },
];

const OPCOES_SITUACAO_IE = [
  { value: "A", label: "Ativa" },
  { value: "I", label: "Inativa" },
  { value: "S", label: "Suspensa" },
  { value: "N", label: "Inapta" },
];

const OPCOES_FATURAMENTO = [
  { value: "81000", label: "Até R$ 81.000,00 (Anual)" },
  { value: "720000", label: "Até R$ 720.000,00" },
  { value: "1200000", label: "de R$ 720.000,01 até R$ 1.200.000,00" },
  { value: "1440000", label: "de R$ 1.200.000,01 até R$ 1.440.000,00" },
  { value: "2400000", label: "de R$ 1.440.000,01 até R$ 2.400.000,00" },
  { value: "3600000", label: "de R$ 2.400.000,01 até R$ 3.600.000,00" },
  { value: "6000000", label: "de R$ 3.600.000,01 até R$ 6.000.000,00" },
  { value: "12000000", label: "de R$ 6.000.000,01 até R$ 12.000.000,00" },
  { value: "12000001", label: "Acima de R$ 12.000.000,00" },
];

function rotuloOpcao(opcoes, valor) {
  return opcoes.find((opcao) => opcao.value === valor)?.label || valor;
}

/** Um par rótulo/valor. Mostra um placeholder em itálico quando o dado não veio preenchido. */
function Campo({ rotulo, valor, largo }) {
  const vazio = valor === null || valor === undefined || valor === "";
  return (
    <div className={`${styles.campo} ${largo ? styles.campoLargo : ""}`}>
      <span className={styles.rotulo}>{rotulo}</span>
      <span className={vazio ? styles.valorVazio : styles.valor}>{vazio ? "Não informado" : valor}</span>
    </div>
  );
}

/** Mesmo Campo, mas formatando a data (dd/mm/aaaa) quando ela existir. */
function CampoData({ rotulo, valor }) {
  const data = valor ? dayjs(valor) : null;
  const valido = data && data.isValid();
  return <Campo rotulo={rotulo} valor={valido ? data.format("DD/MM/YYYY") : null} />;
}

/** Campo booleano (Sim/Não), mostrado como badge em vez de rádio desabilitado. */
function CampoBadge({ rotulo, valor }) {
  const indefinido = valor === null || valor === undefined;
  return (
    <div className={styles.campo}>
      <span className={styles.rotulo}>{rotulo}</span>
      <span
        className={`${styles.badge} ${
          indefinido ? styles.badgeIndefinido : valor ? styles.badgeSim : styles.badgeNao
        }`}
      >
        {indefinido ? "Não informado" : valor ? "Sim" : "Não"}
      </span>
    </div>
  );
}

export function GroupEmpresa({ info, consultarSituacaoFiscal }) {
  // Mesma lógica original de exibição condicional dos campos por
  // enquadramento — mantida exatamente igual, só não afeta mais inputs de
  // formulário, e sim quais Campos de leitura aparecem.
  const [isMostrarTipoEmpresa, setIsMostrarTipoEmpresa] = useState(true);
  const [isMostrarEnquadramento, setIsMostrarEnquadramento] = useState(true);
  const [isMostrarRegimeTributario, setIsMostrarRegimeTributario] = useState(true);
  const [isMostrarDataOpcaoSN, setIsMostrarDataOpcaoSN] = useState(true);
  const [isMostrarSegmentoAtuacao, setIsMostrarSegmentoAtuacao] = useState(true);
  const [isMostrarDataMigracacao, setIsMostrarDataMigracacao] = useState(false);

  useEffect(() => {
    const validacoesDeCampo = () => {
      if (info.enquadramento === "EPP") {
        setIsMostrarDataOpcaoSN(true);
      }
      if (info.enquadramento === "" || info.enquadramento === null) {
        setIsMostrarDataOpcaoSN(false);
      }
      if (info.enquadramento !== "PA" || info.enquadramento !== "PL") {
        setIsMostrarTipoEmpresa(false);
        setIsMostrarEnquadramento(false);
        setIsMostrarRegimeTributario(false);
        setIsMostrarSegmentoAtuacao(false);
      } else {
        setIsMostrarTipoEmpresa(true);
        setIsMostrarEnquadramento(true);
        setIsMostrarRegimeTributario(true);
        setIsMostrarDataOpcaoSN(true);
        setIsMostrarSegmentoAtuacao(true);
      }

      if (info.dataMigracao === null || info.dataMigracao === "") {
        setIsMostrarDataMigracacao(false);
      } else {
        setIsMostrarDataMigracacao(true);
      }
      if (info.situacaoId === "S" && info.tipoId === "4") {
        setIsMostrarDataOpcaoSN(true);
      }
    };

    validacoesDeCampo();
  });

  return (
    <div className={styles.painel}>
      {info.cnpj && (
        <div className={styles.cabecalhoAcao}>
          <Tooltip placement="top" title="Consultar situação fiscal da empresa">
            <button
              type="button"
              className={styles.botaoIcone}
              onClick={() => consultarSituacaoFiscal(info.clienteId, info.cnpj)}
            >
              <FiFileText />
            </button>
          </Tooltip>
        </div>
      )}

      <div className={styles.grupo}>
        <h4 className={styles.grupoTitulo}>Identificação</h4>
        <div className={styles.grade}>
          <Campo rotulo="CNPJ" valor={info.cnpjEmpresa ? formatCNPJ(info.cnpjEmpresa) : null} />
          <Campo rotulo="Razão Social" valor={info.razaoSocialEmpresa} largo />
          <Campo rotulo="Nome Fantasia" valor={info.nomeFantasia} />
        </div>
      </div>

      <div className={styles.grupo}>
        <h4 className={styles.grupoTitulo}>Registros e inscrições</h4>
        <div className={styles.grade}>
          <Campo rotulo="NIRE" valor={info.nire} />
          <Campo rotulo="Contribuição Estadual (ICMS)" valor={info.contribuicaoEstadual} />
          <Campo rotulo="Inscrição Estadual" valor={info.inscricaoEstadual} />
          <Campo rotulo="Situação IE" valor={rotuloOpcao(OPCOES_SITUACAO_IE, info.situacaoIE)} />
          <Campo rotulo="Inscrição Municipal" valor={info.incricaoMunicipal} />
          <Campo rotulo="Inscrição Imobiliária" valor={info.inscricaoImobiliaria} largo />
        </div>
      </div>

      <div className={styles.grupo}>
        <h4 className={styles.grupoTitulo}>Enquadramento e tributação</h4>
        <div className={styles.grade}>
          {isMostrarTipoEmpresa && <Campo rotulo="Tipo Empresa" valor={info.tipoEmpresa} />}
          {isMostrarEnquadramento && <Campo rotulo="Enquadramento" valor={rotuloOpcao(OPCOES_ENQUADRAMENTO, info.enquadramento)} />}
          {isMostrarRegimeTributario && (
            <Campo rotulo="Regime Tributário" valor={rotuloOpcao(OPCOES_REGIME_TRIBUTARIO, info.regimeTributario)} />
          )}
          <CampoBadge rotulo="Empresa optante do Simples Nacional?" valor={info.simplesNacional} />
          <CampoData rotulo="Data Análise SN" valor={info.dataPedidoSN} />
          {isMostrarDataOpcaoSN && <CampoData rotulo="Data Opção SN" valor={info.dataOpcaoSN} />}
          {isMostrarDataMigracacao && <CampoData rotulo="Data de Migração" valor={info.dataMigracao} />}
        </div>
      </div>

      <div className={styles.grupo}>
        <h4 className={styles.grupoTitulo}>Atividade e porte</h4>
        <div className={styles.grade}>
          {isMostrarSegmentoAtuacao && <Campo rotulo="Segmento de Atuação" valor={info.segmentoAtuacao} />}
          <Campo rotulo="Número de Funcionários" valor={info.quantidadeFuncionarios} />
          <Campo rotulo="Faixa de Faturamento" valor={rotuloOpcao(OPCOES_FATURAMENTO, info.faixaFaturamento)} />
          <Campo rotulo="Forma de Atuação" valor={info.formaAtuacao} />
          <Campo rotulo="Atividade" valor={info.descricaoAtividades} largo />
          <Campo rotulo="Capital Social" valor={info.capitalSocial != null ? formatValueBrl(info.capitalSocial) : null} />
        </div>
      </div>

      <div className={styles.grupo}>
        <h4 className={styles.grupoTitulo}>Endereço</h4>
        <div className={styles.grade}>
          <Campo rotulo="Logradouro" valor={info.logradouro} largo />
          <Campo rotulo="Número" valor={info.numero} />
          <Campo rotulo="Complemento" valor={info.complemento} />
          <Campo rotulo="Bairro" valor={info.bairro} />
          <Campo rotulo="Cidade" valor={info.cidade} largo />
          <Campo rotulo="Estado" valor={info.estado} />
          <Campo rotulo="CEP" valor={info.cep ? formatCEP(info.cep) : null} />
        </div>
      </div>
    </div>
  );
}

export default GroupEmpresa;
